import type { LayoutNode } from '../../../components/oasis/bespoke/layout/types';
import type { Question } from '../../../components/oasis/types';
import { collectSectionCoverageQids } from '../../../components/oasis/bespoke/verification/bespokeCoverageSearch';
import { client } from '../../../services/api';
import {
  getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost,
  listBespokeTemplatesBespokeTemplatesGet,
} from '../../../services/api/sdk.gen';
import type {
  BespokeTemplateResponse,
  FormSchemaVersion,
  FormSchemaVersionPin,
  RogerDocFormTypeCatalogEntry,
  VersionedQuestionSchema,
} from '../../../services/api/types.gen';
import type { FormTree, FormTreeQuestion } from '../formSchemaTypes';
import { formatSectionDropdownLabel } from '../formatSectionDropdownLabel';
import {
  bespokeTemplateMatchesPin,
  formSchemaVersionPinKey,
  formSchemaVersionPinLabel,
  pinFromBespokeTemplate,
} from '../formSchemaVersionPin';
import {
  layoutFieldQidSignatureHexFromQuestions,
  schemaVersionLabel,
  validateLayoutJsonForSection,
} from '../formSchemaUtils';
import { patchBespokeTemplateLayout } from '../matchingLayoutCopyShared';
import { spliceSectionPortLayout } from './sectionLayoutPortSplice';

export type SectionPortCandidate = {
  template: BespokeTemplateResponse;
  schemaVersion: FormSchemaVersion;
  sectionName: string;
  displayLabel: string;
  /** QIDs present in both source and destination section schemas. */
  sharedQids: string[];
  /** Destination-only schema QIDs (must be preserved by the port). */
  destOnlyQids: string[];
  /** Source-only schema QIDs (absent from destination). */
  sourceOnlyQids: string[];
  destQuestions: FormTreeQuestion[];
  /** True when destination layout_json deep-equals the source layout. */
  layoutAlreadyMatches: boolean;
  /** True when destination section schema signature matches the source (same QID set). */
  exactSchemaMatch: boolean;
  /** SHA-256 hex of destination section schema QIDs (`layout_field_qid_signature` basis). */
  destSchemaSignature: string;
  /** Deterministic splice result for this destination. */
  resultLayout: LayoutNode[];
  /**
   * True when the splice result deep-equals this destination's current
   * layout_json (already up to date for the port).
   */
  portResultMatchesExistingLayout: boolean;
  /** Soft validation warning for {@link resultLayout}, if any. */
  validationWarning?: string;
};

export type SectionPortCandidateGroup = {
  destSchemaSignature: string;
  /** Any member — same schema/overlap for all members in the group. */
  representative: SectionPortCandidate;
  members: SectionPortCandidate[];
};

export type SectionPortDiscoveryResult = {
  candidates: SectionPortCandidate[];
  /** Human-readable pins that could not be resolved or fetched. */
  unresolvedVersionNames: string[];
};

/**
 * True when applying the port would actually change this destination.
 *
 * For same-schema groups that share one preview write layout, prefer
 * {@link groupCandidatesForSharedPreviewWork}: it groups first, binds every
 * member to the representative preview, then filters with this helper so
 * "needs work" means "differs from the layout the dialog will save."
 */
export function candidateNeedsPortWork(
  candidate: SectionPortCandidate
): boolean {
  return (
    !candidate.layoutAlreadyMatches &&
    !candidate.portResultMatchesExistingLayout
  );
}

/**
 * JSON.stringify with object keys sorted recursively, so two structurally
 * equal values serialize identically regardless of key insertion order.
 * Needed because `layout_json` is a JSONB column and Postgres does not
 * preserve key order on write — a freshly built layout and its stored copy
 * can carry the same keys in a different order.
 */
function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.fromEntries(
        Object.keys(val as Record<string, unknown>)
          .sort()
          .map(k => [k, (val as Record<string, unknown>)[k]])
      );
    }
    return val;
  });
}

/** Key-order-insensitive deep equality for layout_json trees. */
export function layoutNodesDeepEqual(a: unknown, b: unknown): boolean {
  return stableJson(a) === stableJson(b);
}

/**
 * Count group members whose live layout already matches the group's shared
 * write layout ({@link SectionPortCandidate.resultLayout} after
 * {@link bindGroupToSharedPreviewLayout}).
 */
export function countGroupMembersMatchingLayout(
  group: SectionPortCandidateGroup
): number {
  return group.members.filter(m => m.portResultMatchesExistingLayout).length;
}

/**
 * Same-schema groups share one write layout: the representative's splice
 * preview. Remap every member onto that layout and recompute
 * `portResultMatchesExistingLayout` against it so "needs work" / Copy both
 * mean "write what the preview shows."
 *
 * Intentional, not a gap: this overwrites a member's `resultLayout` even if
 * that destination's live layout has independently drifted from the
 * representative's arrangement — including any destination-only content
 * (`destOnlyQids`) that member had spliced in on its own. The point of
 * "Copy to similar sections" is to make every destination sharing a schema
 * look the same, so collapsing per-destination drift (arrangement *or*
 * destination-only content) onto one consistent layout is the desired
 * outcome. Do not special-case "preserve this destination's own
 * arrangement/content" — that would defeat the purpose of the feature.
 */
export function bindGroupToSharedPreviewLayout(
  group: SectionPortCandidateGroup
): SectionPortCandidateGroup {
  const sharedLayout = group.representative.resultLayout;
  const validationWarning = group.representative.validationWarning;
  const members = group.members.map(m => {
    const destLayout = Array.isArray(m.template.layout_json)
      ? (m.template.layout_json as LayoutNode[])
      : [];
    return {
      ...m,
      resultLayout: sharedLayout,
      portResultMatchesExistingLayout: layoutNodesDeepEqual(
        sharedLayout,
        destLayout
      ),
      validationWarning,
    };
  });
  return {
    destSchemaSignature: group.destSchemaSignature,
    representative: members[0],
    members,
  };
}

/**
 * Group candidates by destination schema and bind each group to one shared
 * preview layout — no filtering, so members that already match the shared
 * layout are still included. Use this (rather than
 * {@link groupCandidatesForSharedPreviewWork}) when you need the *full*
 * membership of a signature group, e.g. to report "N of M destinations in
 * this group already have the new layout."
 */
export function bindCandidatesToSharedPreviewGroups(
  candidates: SectionPortCandidate[]
): SectionPortCandidateGroup[] {
  return groupSectionPortCandidatesByDestSignature(candidates).map(
    bindGroupToSharedPreviewLayout
  );
}

/**
 * Group candidates by destination schema, bind each group to one shared
 * preview layout, then drop members that already match that layout (or the
 * source). Empty groups are omitted.
 */
export function groupCandidatesForSharedPreviewWork(
  candidates: SectionPortCandidate[]
): SectionPortCandidateGroup[] {
  return bindCandidatesToSharedPreviewGroups(candidates)
    .map(group => {
      const members = group.members.filter(candidateNeedsPortWork);
      if (members.length === 0) return null;
      return {
        destSchemaSignature: group.destSchemaSignature,
        representative: members[0],
        members,
      } satisfies SectionPortCandidateGroup;
    })
    .filter((group): group is SectionPortCandidateGroup => group != null);
}

/** Exported for unit tests — shared / dest-only / source-only QID sets. */
export function computeSectionQidOverlap(
  sourceQids: string[],
  destQuestions: FormTreeQuestion[]
): {
  sharedQids: string[];
  destOnlyQids: string[];
  sourceOnlyQids: string[];
} | null {
  const destQids = collectSectionCoverageQids(destQuestions as Question[]);
  const destSet = new Set(destQids);
  const sourceSet = new Set(sourceQids);
  const sharedQids = sourceQids.filter(q => destSet.has(q)).sort();
  if (sharedQids.length === 0) return null;
  return {
    sharedQids,
    destOnlyQids: destQids.filter(q => !sourceSet.has(q)).sort(),
    sourceOnlyQids: sourceQids.filter(q => !destSet.has(q)).sort(),
  };
}

/** Group destinations that share the same section schema signature. */
export function groupSectionPortCandidatesByDestSignature(
  candidates: SectionPortCandidate[]
): SectionPortCandidateGroup[] {
  const bySig = new Map<string, SectionPortCandidate[]>();
  for (const c of candidates) {
    const list = bySig.get(c.destSchemaSignature) ?? [];
    list.push(c);
    bySig.set(c.destSchemaSignature, list);
  }
  const groups: SectionPortCandidateGroup[] = [];
  for (const [destSchemaSignature, members] of bySig) {
    members.sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
    groups.push({
      destSchemaSignature,
      representative: members[0],
      members,
    });
  }
  groups.sort((a, b) => {
    if (b.members.length !== a.members.length) {
      return b.members.length - a.members.length;
    }
    return a.representative.displayLabel.localeCompare(
      b.representative.displayLabel
    );
  });
  return groups;
}

type DiscoverOptions = {
  /** Full RogerDoc pin for the source template — names alone are not unique. */
  sourcePin: FormSchemaVersionPin;
  sourceSectionName: string;
  sourceSectionSchema: FormTreeQuestion[];
  sourceLayout: LayoutNode[];
  resolveVersionByPin: (pin: FormSchemaVersionPin) => FormSchemaVersion | null;
  fetchSchemaForVersion: (
    version: FormSchemaVersion
  ) => Promise<FormTree | null>;
};

/**
 * Find live templates for the same section_name with any shared schema QIDs.
 * Each candidate includes a precomputed deterministic splice result + soft
 * validation warning.
 *
 * Destination schemas are resolved and cached by full pin
 * (`form_type` + `schema_version_name` + `form_schema_version`) so reused
 * names across form types / versions never share the wrong question tree.
 */
export async function discoverSectionPortCandidates(
  options: DiscoverOptions
): Promise<SectionPortDiscoveryResult> {
  const {
    sourcePin,
    sourceSectionName,
    sourceSectionSchema,
    sourceLayout,
    resolveVersionByPin,
    fetchSchemaForVersion,
  } = options;

  const sourceQids = collectSectionCoverageQids(
    sourceSectionSchema as Question[]
  );
  const sourceLayoutJson = stableJson(sourceLayout);
  const sourceSchemaSignature =
    await layoutFieldQidSignatureHexFromQuestions(sourceSectionSchema);
  const destSchemaSignatureByKey = new Map<string, string>();

  const res = await listBespokeTemplatesBespokeTemplatesGet({
    query: { status: 'live' },
    client,
    throwOnError: true,
  });

  const liveTemplates = (res.data ?? []).filter(
    t =>
      Array.isArray(t.layout_json) &&
      t.section_name === sourceSectionName &&
      !bespokeTemplateMatchesPin(t, sourcePin)
  );

  const unresolved = new Set<string>();
  const schemaByPinKey = new Map<string, FormTree | null>();
  const uniquePins = new Map<string, FormSchemaVersionPin>();
  for (const template of liveTemplates) {
    const pin = pinFromBespokeTemplate(template);
    uniquePins.set(formSchemaVersionPinKey(pin), pin);
  }
  await Promise.all(
    [...uniquePins.entries()].map(async ([pinKey, pin]) => {
      const label = formSchemaVersionPinLabel(pin);
      const version = resolveVersionByPin(pin);
      if (!version) {
        unresolved.add(label);
        schemaByPinKey.set(pinKey, null);
        return;
      }
      let tree: FormTree | null = null;
      try {
        tree = await fetchSchemaForVersion(version);
      } catch {
        tree = null;
      }
      if (!tree) unresolved.add(label);
      schemaByPinKey.set(pinKey, tree);
    })
  );

  const candidates: SectionPortCandidate[] = [];
  for (const template of liveTemplates) {
    const pin = pinFromBespokeTemplate(template);
    const pinKey = formSchemaVersionPinKey(pin);
    const tree = schemaByPinKey.get(pinKey) ?? null;
    const destQuestions = tree?.[template.section_name];
    if (!destQuestions?.length) continue;
    const overlap = computeSectionQidOverlap(sourceQids, destQuestions);
    if (!overlap) continue;

    const destSchemaKey = `${pinKey}::${template.section_name}`;
    let destSchemaSignature = destSchemaSignatureByKey.get(destSchemaKey);
    if (!destSchemaSignature) {
      destSchemaSignature =
        await layoutFieldQidSignatureHexFromQuestions(destQuestions);
      destSchemaSignatureByKey.set(destSchemaKey, destSchemaSignature);
    }
    const exactSchemaMatch = destSchemaSignature === sourceSchemaSignature;

    const destLayout = Array.isArray(template.layout_json)
      ? (template.layout_json as LayoutNode[])
      : [];
    const resultLayout = spliceSectionPortLayout({
      sourceLayout,
      destLayout,
      sharedQids: overlap.sharedQids,
      sourceOnlyQids: overlap.sourceOnlyQids,
      destOnlyQids: overlap.destOnlyQids,
    });
    // The splice is a rebuild from source's shared-content arrangement (see
    // sectionLayoutPortSplice.ts module doc), so it's a structural fixed
    // point: re-splicing an already-ported destination reproduces the same
    // tree. The comparison is key-order-insensitive because destLayout comes
    // from a JSONB column whose key order Postgres does not preserve.
    const portResultMatchesExistingLayout = layoutNodesDeepEqual(
      resultLayout,
      destLayout
    );
    const validation = validateLayoutJsonForSection(
      JSON.stringify(resultLayout),
      destQuestions
    );

    const schemaVersion: FormSchemaVersion = resolveVersionByPin(pin) ?? {
      name: pin.schema_version_name,
      version: pin.form_schema_version,
      form_type: pin.form_type,
    };

    candidates.push({
      template,
      schemaVersion,
      sectionName: template.section_name,
      displayLabel: `${schemaVersionLabel(schemaVersion)} · ${formatSectionDropdownLabel(
        template.section_name
      )}`,
      sharedQids: overlap.sharedQids,
      destOnlyQids: overlap.destOnlyQids,
      sourceOnlyQids: overlap.sourceOnlyQids,
      destQuestions,
      layoutAlreadyMatches:
        stableJson(template.layout_json) === sourceLayoutJson,
      exactSchemaMatch,
      destSchemaSignature,
      resultLayout,
      portResultMatchesExistingLayout,
      validationWarning: validation.ok ? undefined : validation.error,
    });
  }

  candidates.sort((a, b) => {
    if (a.layoutAlreadyMatches !== b.layoutAlreadyMatches) {
      return a.layoutAlreadyMatches ? 1 : -1;
    }
    if (a.exactSchemaMatch !== b.exactSchemaMatch) {
      return a.exactSchemaMatch ? -1 : 1;
    }
    if (b.sharedQids.length !== a.sharedQids.length) {
      return b.sharedQids.length - a.sharedQids.length;
    }
    return a.displayLabel.localeCompare(b.displayLabel);
  });

  return {
    candidates,
    unresolvedVersionNames: [...unresolved],
  };
}

export async function fetchSchemaTreeForVersion(
  version: FormSchemaVersion,
  formTypeCatalog: RogerDocFormTypeCatalogEntry[]
): Promise<FormTree | null> {
  const entry = formTypeCatalog.find(c => c.form_type === version.form_type);
  const visitType = entry?.canonical_visit_type_id;
  if (!visitType) return null;
  const res = await getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost({
    query: { visit_type: visitType },
    body: {
      schema_version: {
        name: version.name,
        version: version.version ?? null,
      },
    },
    client,
  });
  const payload = res?.data as VersionedQuestionSchema | undefined;
  return (payload?.schema_data as FormTree | undefined) ?? null;
}

/**
 * Write a layout to a candidate's live template.
 *
 * Defaults to the candidate's precomputed `resultLayout`. Callers that apply
 * a same-schema group should pass the shared preview layout so every member
 * receives the layout shown in the dialog.
 *
 * Delegates to {@link patchBespokeTemplateLayout} so the validate → PATCH →
 * return-saved-row contract is shared with the matching-copy flow. Fails
 * closed: does not PATCH when the layout fails section validation.
 * The destination is already live (discovery filters on `status: 'live'`), so
 * no status is sent — the PATCH only rewrites the layout.
 *
 * Returns the saved row so callers can patch local state directly instead of
 * refetching the (unscoped, potentially large) full template list.
 */
export async function applySectionPortLayout(options: {
  candidate: SectionPortCandidate;
  /** When set, write this layout instead of `candidate.resultLayout`. */
  layout?: LayoutNode[];
  /**
   * Persist even when section validation fails. Only for the dialog's
   * explicit "copy anyway" override — default remains fail-closed.
   *
   * Intentional, not a gap: the dialog applies this flag to every member of
   * the selected group, not just whichever member's warning is shown (the
   * group shares one representative preview via
   * {@link bindGroupToSharedPreviewLayout}, so there is no per-member
   * warning to distinguish). "Copy anyway" is a group-level acknowledgment —
   * do not add per-member validation/opt-in here.
   */
  allowInvalidLayout?: boolean;
}): Promise<BespokeTemplateResponse> {
  const { candidate, layout, allowInvalidLayout = false } = options;
  const layoutNodes = layout ?? candidate.resultLayout;
  return patchBespokeTemplateLayout({
    templateId: candidate.template.id,
    layoutJson: JSON.stringify(layoutNodes),
    questions: candidate.destQuestions,
    name: candidate.template.name,
    skipValidation: allowInvalidLayout,
  });
}
